import { Dispatch } from 'redux';
import { cloneDeep, isString } from 'lodash';

import * as api from '../utils/api';
import { RootState } from './store';
import { showModal } from './modal';
import connectWebSocket, { closeWebSocket } from '../utils/websocket';
import { setActiveTeams, setActiveDeselectedCourses } from './features/teamsSlice';
import { hasTeamManagerRole, isPlatformAdmin, processError } from '../utils/functions';
import { getActiveDeselectedCourses, getActiveTeams } from '../utils/reports';

interface AuthState {
  authenticatedUser?: IUser;
  isAuthenticated?: boolean;
  logoutType?: ValueOf<ILogoutTypes>;
  maintenanceLogout?: boolean;
  maintenanceNotified?: boolean;
}

const actions = {
  UPDATE: 'lms-ui/auth/UPDATE',
  AUTH_USER: 'lms-ui/auth/AUTH_USER',
  UPDATE_AUTH_USER_PROPS: 'lms-ui/auth/UPDATE_AUTH_USER_PROPS',
};

interface AuthAction {
  type: typeof actions.AUTH_USER;
  payload?: IUser;
}

type AuthActionTypes = AuthAction;

const initialState: AuthState = {
  authenticatedUser: undefined,
  isAuthenticated: false,
  logoutType: undefined,
  maintenanceLogout: false,
  maintenanceNotified: false,
};

const update = (payload: AuthState) => {
  return { type: actions.UPDATE, payload };
};

export const updateAuthenticated = (authenticatedUser?: IUser) => {
  return { type: actions.AUTH_USER, payload: authenticatedUser };
};

export const updateAuthUserProps = (payload: Partial<IUser>) => {
  return { type: actions.UPDATE_AUTH_USER_PROPS, payload };
};

/**
 * Update the active teams and courses based on the user's role
 * @param user - The authenticated user
 * @param dispatch
 */
export const updateActiveTeamsAndCourses = (user: IUser, dispatch: Dispatch) => {
  const isTeamManager = hasTeamManagerRole(user);
  const activeTeams = getActiveTeams(user, isTeamManager);
  const activeDeselectedCourses = getActiveDeselectedCourses(user, isTeamManager);

  dispatch(setActiveTeams(activeTeams));
  dispatch(setActiveDeselectedCourses(activeDeselectedCourses));
};

const checkWebSocket = async (dispatch: Dispatch, user?: IUser) => {
  if (user) {
    const isAdmin = isPlatformAdmin(user);
    const response = await api.getWSToken();
    connectWebSocket(user._id as string, response.data.token, dispatch, isAdmin);
  } else {
    closeWebSocket();
  }
};

export const checkAuthed = () => async (dispatch: Dispatch) => {
  try {
    let response = await api.checkAuthed();
    const user = response.data;
    await checkWebSocket(dispatch, user);
    dispatch(updateAuthenticated(user));
    updateActiveTeamsAndCourses(user, dispatch);
  } catch (e) {
    dispatch(updateAuthenticated(undefined));
  }
};

export const loginBasic = (data: LoginData) => async (dispatch: Dispatch) => {
  try {
    const response = await api.loginBasic(data);
    const user = response.data;
    await checkWebSocket(dispatch, user);
    dispatch(update({ authenticatedUser: user, isAuthenticated: true, logoutType: undefined }));
  } catch (e) {
    dispatch(updateAuthenticated(undefined));
    const finalError = processError(e, dispatch);
    return Promise.reject(finalError);
  }
};

export const logout = (logoutType: ValueOf<ILogoutTypes>) => async (dispatch: Dispatch) => {
  try {
    await api.logout();
    dispatch(showModal());
    closeWebSocket();
  } catch (e) {
    console.error(e);
  } finally {
    dispatch(update({ isAuthenticated: false, authenticatedUser: undefined, logoutType }));
  }
};

export const updateMaintenanceNotified = (notified: boolean) => async (dispatch: Dispatch) => {
  dispatch(update({ maintenanceNotified: notified }));
};

/**
 * Updates the authenticated user's course data with the provided course.
 * @param course Data to update the user's course with.
 * @returns
 */
export const updateCourse =
  (course: ICourse) => async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const currentAuthedUser = cloneDeep(state.auth.authenticatedUser);
    if (!currentAuthedUser) return;

    const index =
      (currentAuthedUser.courses as ICourse[])?.findIndex((c) => c._id === course._id) ?? -1;
    if (index > -1) {
      currentAuthedUser.courses![index] = course;
      dispatch(updateAuthenticated(currentAuthedUser));
    }
  };

export default function reducer(state = initialState, action: AuthActionTypes) {
  switch (action.type) {
    case actions.UPDATE: {
      return { ...state, ...action.payload };
    }
    case actions.AUTH_USER: {
      return {
        ...state,
        authenticatedUser: action.payload,
        isAuthenticated: !!action.payload,
      };
    }
    case actions.UPDATE_AUTH_USER_PROPS: {
      return {
        ...state,
        authenticatedUser: { ...state.authenticatedUser, ...action.payload },
      };
    }
    default:
      return state;
  }
}
